import { apiClient } from '../utils/api-client';
import type { BoardHold } from '../types/climb';
import type {
  BluetoothScan,
  BluetoothScanResponse,
  Board,
  BoardCalibration,
  BoardCircuitSession,
  BoardCircuitSessionResponse,
  BoardClimbsListResponse,
  BoardClimbsPage,
  BoardClimbsQuery,
  BoardObservation,
  BoardObservationResponse,
  BoardPayload,
  BoardDisabledCells,
  BoardExtraHoldMap,
  BoardImageCrop,
  BoardLedLine,
  BoardLightCommand,
  BoardLightResponse,
  BoardResponse,
  BoardsListResponse,
  BoardShapes,
  BoardType,
  BoardTypesListResponse,
  GradingSystemRef,
  Device,
  DevicePayload,
  DeviceResponse,
  DevicesListResponse,
  DeviceSession,
  DeviceSessionResponse,
} from '../types/device';

interface ApiErrorBody {
  errors?: Array<{
    detail: string;
    code?: string;
    meta?: { climb_name?: string; user_name?: string; expires_at?: string };
  }>;
}

async function throwForResponse(response: Response, fallback: string): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
  throw new Error(body.errors?.[0]?.detail || fallback);
}

/**
 * Another climber is already playing a circuit on this board. Carries who and
 * what, because the answer — interrupt them or not — is one only the person
 * standing at the wall can give.
 */
export class CircuitInProgressError extends Error {
  readonly climbName?: string;
  readonly userName?: string;

  constructor(message: string, holder: { climbName?: string; userName?: string }) {
    super(message);
    this.name = 'CircuitInProgressError';
    this.climbName = holder.climbName;
    this.userName = holder.userName;
  }
}

/** What a light command can say beyond which holds to light. */
export interface LightOptions {
  /**
   * Whether the device holds the link open after this command, so the next one
   * is instant instead of paying the BLE connect again. Left out, the board's
   * own setting decides (see Board.resolved_keep_alive).
   *
   * It describes this one command, not a mode: every command of a sequence has
   * to repeat it, and the one that ends the session must pass `false` — a board
   * whose link is held is unreachable for everybody else until it is let go.
   */
  keepAlive?: boolean;
  /**
   * Index, in the circuit's holds, of the one to leave flashing — the circuit
   * player's "go here next". The whole problem still lights up; the server
   * flashes that one hold on and off behind the response, and drops the job as
   * soon as the board is sent anything else. Only a saved circuit has one, and
   * a start hold never flashes.
   */
  blinkHold?: number | null;
  /**
   * That this command is one step of a circuit run rather than somebody asking
   * to see the problem. The wall treats it the same; the server only uses it to
   * keep its "times lit" count honest, since a run is one lighting and not one
   * per hold. Not inferable from `blinkHold`: the step onto a start hold and
   * the command that releases the board at the end both carry none.
   */
  circuitStep?: boolean;
  /**
   * Who carries the command out. `device` — the default — is the box on the
   * wall, reached over MQTT. `local` is this browser doing the box's job on a
   * board that has none: nothing is published, and the encoded command comes
   * back for the page to write over Web Bluetooth (see board-bluetooth.ts).
   */
  transport?: 'device' | 'local';
}

/** The light attributes shared by a saved problem and a live preview. */
function lightAttributes({ keepAlive, blinkHold, circuitStep, transport }: LightOptions): Record<string, unknown> {
  return {
    ...(keepAlive === undefined ? {} : { keep_alive: keepAlive }),
    ...(blinkHold == null ? {} : { blink_hold: blinkHold }),
    ...(circuitStep ? { circuit_step: true } : {}),
    ...(transport ? { transport } : {}),
  };
}

/**
 * The gym's proxy devices and the boards they drive. There is no create or
 * delete for devices: they are physical hardware DivePeaks assigns, so a gym
 * only lists, renames and configures the ones it already has.
 */
class DevicesService {
  async list(gymSlug: string): Promise<Device[]> {
    const response = await apiClient.get(`/api/gyms/${gymSlug}/devices`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch devices');
    }

    const data: DevicesListResponse = await response.json();
    return data.data.map((item) => item.attributes);
  }

  /**
   * The boards installed at a gym, flattened across its devices — what the
   * gym profile shows as per-board tabs. Read-only and behind the :boards
   * feature flag, so only a user who has authorized at a device can read it.
   */
  async listGymBoards(gymSlug: string): Promise<Board[]> {
    const response = await apiClient.get(`/api/gyms/${gymSlug}/boards`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch boards');
    }

    const data: BoardsListResponse = await response.json();
    return data.data.map((item) => item.attributes);
  }

  /**
   * The problems on one board, a page at a time.
   *
   * Not `climbsService.searchClimbs`: a board list filters on the hold sets the
   * wall was built from, on whether you have sent a problem and on how many
   * people have, none of which the search index carries — and a Moonboard's
   * shared library is far too long to hand over in one go.
   */
  async listBoardClimbs(
    gymSlug: string,
    boardId: string,
    params: BoardClimbsQuery = {}
  ): Promise<BoardClimbsPage> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.append(key, String(value));
    });

    const suffix = query.toString() ? `?${query}` : '';
    const response = await apiClient.get(`/api/gyms/${gymSlug}/boards/${boardId}/climbs${suffix}`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch board climbs');
    }

    const body: BoardClimbsListResponse = await response.json();

    return {
      climbs: body.data.map((item) => {
        const climb = item.attributes;
        // The sends and the reader's attempts ride in `relationships`, but
        // ClimbRow reads them off the climb — same unwrapping as climbsService.
        if (item.relationships?.sends?.data) climb.sends = item.relationships.sends.data;
        if (item.relationships?.user_attempts?.data) climb.user_attempts = item.relationships.user_attempts.data;
        return climb;
      }),
      hasMore: body.meta?.has_more ?? false,
      inclinations: body.meta?.inclinations ?? [],
    };
  }

  /**
   * Lights a board climb's holds on the wall. The server encodes the problem
   * for the board's protocol and hands it to the board's device over MQTT, so
   * a resolved promise only means the command left — the LEDs themselves are
   * out of our reach. Only offered when the board reports `can_light`.
   *
   * With `transport: 'local'` nothing is published: the encoded command comes
   * back instead, for a page holding its own Bluetooth link to write. That is
   * the answer for a board no device drives (`can_light_locally`), and the only
   * case where this returns anything.
   */
  async lightBoardClimb(
    gymSlug: string,
    boardId: string,
    climbId: string,
    options: LightOptions = {}
  ): Promise<BoardLightCommand | null> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/boards/${boardId}/light`, {
      data: { attributes: { climb_id: climbId, ...lightAttributes(options) } },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to light the board');
    }

    if (options.transport !== 'local') return null;

    const body: BoardLightResponse = await response.json();
    return body.data.attributes.command ?? null;
  }

  /**
   * Lights a selection of holds that is not a saved problem yet — the live
   * preview while a setter draws on the create page. Same command and same
   * caveat as `lightBoardClimb`; the holds travel with the request because
   * there is no climb to name.
   */
  async previewBoardLights(
    gymSlug: string,
    boardId: string,
    holds: BoardHold[],
    options: LightOptions = {}
  ): Promise<void> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/boards/${boardId}/light`, {
      data: { attributes: { holds, ...lightAttributes(options) } },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to light the board');
    }
  }

  /**
   * What the board is showing right now, when it was the climber's own
   * Moonboard or Kilter app that lit it — the problem if we recognised it, the
   * lit holds either way.
   *
   * Null means the board has no current problem: nothing has been lit through
   * the proxy, or what was is past the server's window. A failure is answered
   * the same way, on purpose: this is a nicety on top of the board list, and it
   * must never be what stops the list being shown.
   */
  async getBoardObservation(gymSlug: string, boardId: string): Promise<BoardObservation | null> {
    const response = await apiClient.get(`/api/gyms/${gymSlug}/boards/${boardId}/observation`);

    if (!response.ok) return null;

    const body: BoardObservationResponse = await response.json();
    return body.data?.attributes ?? null;
  }

  /**
   * Takes the board to play a circuit on it. Throws CircuitInProgressError when
   * another climber has it, which `force` then overrides — their claim is
   * dropped and their player finds out on its next ping.
   */
  async startCircuitSession(
    gymSlug: string,
    boardId: string,
    climbId: string,
    { force = false }: { force?: boolean } = {}
  ): Promise<BoardCircuitSession> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/boards/${boardId}/circuit_session`,
      { data: { attributes: { climb_id: climbId, force } } }
    );

    if (response.status === 409) {
      const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
      const error = body.errors?.[0];
      throw new CircuitInProgressError(error?.detail || 'A circuit is already running', {
        climbName: error?.meta?.climb_name,
        userName: error?.meta?.user_name,
      });
    }

    if (!response.ok) {
      await throwForResponse(response, 'Failed to start the circuit');
    }

    const data: BoardCircuitSessionResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Says the app is still open on the circuit. Answers false when the claim is
   * gone — expired while the app was away, or taken over by a climber who chose
   * to interrupt — which is the player's cue to stop the run.
   */
  async pingCircuitSession(gymSlug: string, boardId: string, token: string): Promise<boolean> {
    const response = await apiClient.patch(
      `/api/gyms/${gymSlug}/boards/${boardId}/circuit_session`,
      { data: { attributes: { token } } }
    );

    if (response.status === 409 || response.status === 404) return false;

    if (!response.ok) {
      await throwForResponse(response, 'Failed to keep the circuit alive');
    }

    return true;
  }

  /**
   * Gives the board back. The token rides in the query string because a DELETE
   * carries no body; a claim already taken over is left alone.
   */
  async stopCircuitSession(gymSlug: string, boardId: string, token: string): Promise<void> {
    await apiClient.delete(
      `/api/gyms/${gymSlug}/boards/${boardId}/circuit_session?token=${encodeURIComponent(token)}`
    );
  }

  async get(gymSlug: string, id: string): Promise<Device> {
    const response = await apiClient.get(`/api/gyms/${gymSlug}/devices/${id}`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the device');
    }

    const data: DeviceResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Exchanges a device's current OTP for a short-lived usage pass. Public — the
   * caller may be anonymous — so it is the OTP, read off the device, that
   * proves the person is actually there.
   */
  async authorize(gymSlug: string, code: string, otp: string): Promise<DeviceSession> {
    const response = await apiClient.post(`/api/devices/${code}/auth`, {
      gym_slug: gymSlug,
      otp,
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to authorize');
    }

    const data: DeviceSessionResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Renames a device and configures its Bluetooth proxy. Sends only the fields
   * given, so switching the proxy off does not have to resend what it was
   * pointed at — an admin turning it back on next week finds it still there.
   */
  async update(gymSlug: string, id: string, payload: DevicePayload): Promise<Device> {
    const response = await apiClient.patch(`/api/gyms/${gymSlug}/devices/${id}`, {
      data: { attributes: payload },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to update the device');
    }

    const data: DeviceResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Sends the current firmware release to a device.
   *
   * Which version goes out is the server's call — always the current release —
   * so there is nothing to pass. Resolving means the command left for the
   * device, not that it installed: the board proves that by reporting the new
   * version at its next provisioning call, which is why the returned device
   * comes back in the `updating` state and the page polls from there.
   */
  async updateFirmware(gymSlug: string, deviceId: string): Promise<Device> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/devices/${deviceId}/firmware_update`,
      { data: { attributes: {} } }
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to send the update');
    }

    const data: DeviceResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Asks a device to sweep for nearby Bluetooth boards. Resolving only means
   * the command reached the device: what it hears arrives later, through
   * `getBluetoothScan`, which the caller polls while `scanning` is true.
   */
  async startBluetoothScan(gymSlug: string, deviceId: string): Promise<BluetoothScan> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/devices/${deviceId}/bluetooth_scan`,
      { data: { attributes: {} } }
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to start the scan');
    }

    const data: BluetoothScanResponse = await response.json();
    return data.data.attributes;
  }

  /** The state of a device's Bluetooth sweep, and what it has found so far. */
  async getBluetoothScan(gymSlug: string, deviceId: string): Promise<BluetoothScan> {
    const response = await apiClient.get(
      `/api/gyms/${gymSlug}/devices/${deviceId}/bluetooth_scan`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the scan results');
    }

    const data: BluetoothScanResponse = await response.json();
    return data.data.attributes;
  }

  /** Boulder grade systems available to a gym, for the board form. */
  async listBoulderGradingSystems(gymSlug: string): Promise<GradingSystemRef[]> {
    const response = await apiClient.get(`/api/gyms/${gymSlug}/boulder_grading_systems`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch grading systems');
    }

    const data: { data: GradingSystemRef[] } = await response.json();
    return data.data;
  }

  /** The board-type catalogue for the board form's picker. */
  async listBoardTypes(): Promise<BoardType[]> {
    const response = await apiClient.get('/api/board_types');

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch board types');
    }

    const data: BoardTypesListResponse = await response.json();
    return data.data.map((item) => item.attributes);
  }

  async createBoard(gymSlug: string, payload: BoardPayload): Promise<Board> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/boards`, {
      data: { attributes: payload },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to create the board');
    }

    const data: BoardResponse = await response.json();
    return data.data.attributes;
  }

  async updateBoard(gymSlug: string, boardId: string, payload: BoardPayload): Promise<Board> {
    const response = await apiClient.patch(`/api/gyms/${gymSlug}/boards/${boardId}`, {
      data: { attributes: payload },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to update the board');
    }

    const data: BoardResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * The wall's layout: where the grid sits on the photo, which of its cells
   * carry nothing, the holds that are not on the grid at all, and the window of
   * the upload the whole thing is measured against.
   *
   * Sent together because they are edited together, on one picture: the extras
   * are placed inside the quad the corners describe and the corners are placed
   * inside the crop, so saving one without the others would store a position
   * measured against a frame that is no longer there. Everything else about the
   * board is left out rather than resent — a partial payload is what keeps a
   * second person's edit to the same board intact.
   */
  async updateBoardGrid(
    gymSlug: string,
    boardId: string,
    {
      calibration,
      extraHolds,
      disabledCells,
      shapes,
      crop,
      imageSignedId,
      deleteClimbs,
      archiveClimbs,
      ledLines,
      ledHolds,
      unlitHolds,
      grid,
      leds,
    }: {
      calibration: BoardCalibration | null;
      extraHolds?: BoardExtraHoldMap | null;
      disabledCells?: BoardDisabledCells | null;
      /** The lines the wall is drawn with, replaced wholesale when sent. */
      shapes?: BoardShapes | null;
      /** Only when it moved: sending it makes the server cut the picture again. */
      crop?: BoardImageCrop;
      /** A new photo for the wall, uploaded straight to storage beforehand. */
      imageSignedId?: string;
      /**
       * Whether to take every problem off the wall. Only ever true because
       * somebody was asked and said so: redrawing a wall leaves its problems
       * pointing at holds that have moved, and there is no honest way to carry
       * them across.
       */
      deleteClimbs?: boolean;
      /**
       * The gym's answer to "a new photo archives every problem on the wall":
       * the server refuses a new photo on a wall with problems without it.
       */
      archiveClimbs?: boolean;
      /**
       * A scanned wall's LED lines, the holds each one took (as the panel
       * threaded them) and the holds with no LED — sent together, since the
       * server reads each against the others.
       */
      ledLines?: BoardLedLine[];
      ledHolds?: number[][];
      unlitHolds?: number[];
      /** A grid wall's own rows and columns, set on "Edit grid". */
      grid?: { rows: number; columns: number };
      /**
       * How a wired wall's LEDs look and run, set where they are drawn (LED
       * strips, Edit grid) rather than in the board form. Only the keys given
       * are sent.
       */
      leds?: Pick<BoardPayload, 'led_brightness' | 'circuit_highlight_color' | 'led_origin' | 'led_direction'>;
    }
  ): Promise<Board> {
    const response = await apiClient.patch(`/api/gyms/${gymSlug}/boards/${boardId}`, {
      data: {
        attributes: {
          calibration,
          // Left out entirely when the caller has nothing to say about them, so
          // a plain re-alignment does not wipe a wall's screw-ons.
          ...(extraHolds === undefined ? {} : { extra_holds: extraHolds ?? {} }),
          ...(disabledCells === undefined ? {} : { disabled_cells: disabledCells ?? [] }),
          ...(shapes === undefined ? {} : { shapes: shapes ?? [] }),
          ...(crop === undefined ? {} : { image_crop: crop }),
          ...(imageSignedId === undefined ? {} : { image_signed_id: imageSignedId }),
          ...(deleteClimbs ? { delete_climbs: true } : {}),
          ...(archiveClimbs ? { archive_climbs: true } : {}),
          ...(ledLines === undefined ? {} : { led_lines: ledLines, led_holds: ledHolds ?? [], unlit_holds: unlitHolds ?? [] }),
          ...(grid ?? {}),
          ...(leds ?? {}),
        },
      },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to update the board grid');
    }

    const data: BoardResponse = await response.json();
    return data.data.attributes;
  }

  async removeBoard(gymSlug: string, boardId: string): Promise<void> {
    const response = await apiClient.delete(`/api/gyms/${gymSlug}/boards/${boardId}`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to delete the board');
    }
  }
}

export const devicesService = new DevicesService();
